import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { features } from "../../config/features";
import {
  validateVerificationHash,
  validateSelfieAnonymous,
  analyzeDocumentAnonymous,
  requestManualReviewAnonymous,
} from "../../actions/email-verification.action";

const STEP = { LOADING: "loading", ERROR: "error", SELFIE: "selfie", DOCUMENT: "document", COMPLETE: "complete" };
const CAM = { INIT: "init", CAMERA: "camera", PREVIEW: "preview", UPLOADING: "uploading", RESULT: "result" };

// ─── Camera Capture ───────────────────────────────────────────────────────────
const CameraCapture = ({ facingMode, onFile, onSkip, title, description, icon }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState(CAM.INIT);
  const [captured, setCaptured] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setStatus(CAM.CAMERA);
    } catch (err) {
      setCameraError(
        err.name === "NotAllowedError"
          ? "Camera access was denied. Please allow camera access in your browser settings."
          : "Could not access the camera."
      );
    }
  }, [facingMode]);

  useEffect(() => {
    if (status === CAM.CAMERA && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [status]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL("image/jpeg", 0.92));
    stopCamera();
    setStatus(CAM.PREVIEW);
  };

  const handleRetake = () => {
    setCaptured(null);
    setStatus(CAM.INIT);
  };

  const handleSubmit = () => {
    if (!captured) return;
    const byteString = atob(captured.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: "image/jpeg" });
    const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
    onFile(file);
  };

  return (
    <div className="flex flex-col items-center gap-5 text-center w-full">
      {/* Init */}
      {status === CAM.INIT && (
        <>
          <div className="w-16 h-16 rounded-full bg-[#f5f7eb] flex items-center justify-center">
            <i className={`fal ${icon} text-[#9aa82d] text-3xl`}></i>
          </div>
          <div>
            <p className="text-lg font-semibold text-gray-800 mb-1">{title}</p>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          {cameraError && (
            <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{cameraError}</div>
          )}
          <div className="flex gap-3 w-full">
            <button
              onClick={startCamera}
              className="flex-1 py-3 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] font-medium"
            >
              Open Camera
            </button>
            <button
              onClick={onSkip}
              className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium"
            >
              Skip
            </button>
          </div>
        </>
      )}

      {/* Camera */}
      {status === CAM.CAMERA && (
        <>
          <div className="w-full rounded-xl overflow-hidden bg-black relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full"
              style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
            />
            {facingMode === "user" && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="rounded-full border-2 border-white border-opacity-60"
                  style={{ width: "45%", height: "75%", boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)" }}
                />
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 text-center">
            {facingMode === "user"
              ? "Centre your face in the oval. Ensure good lighting and look straight at the camera."
              : "Place your ID document in frame. Ensure good lighting and avoid glare."}
          </p>
          <button
            onClick={handleCapture}
            className="w-full py-3 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] font-medium"
          >
            Capture Photo
          </button>
          <canvas ref={canvasRef} className="hidden" />
        </>
      )}

      {/* Preview */}
      {status === CAM.PREVIEW && captured && (
        <>
          <div className="w-full rounded-xl overflow-hidden">
            <img
              src={captured}
              alt="Preview"
              className="w-full"
              style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
            />
          </div>
          <div className="flex gap-3 w-full">
            <button
              onClick={handleRetake}
              className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium"
            >
              Retake
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-3 rounded-lg text-white bg-[#9aa82d] hover:bg-[#7d8a1f] font-medium"
            >
              Verify and Send
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Mobile Page ─────────────────────────────────────────────────────────
export const VerifyMobilePage = () => {
  const { id, hash } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.LOADING);
  const [firstName, setFirstName] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [manualReviewRequested, setManualReviewRequested] = useState(false);
  const [selfieResult, setSelfieResult] = useState(null);
  const [docResult, setDocResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Validate hash on mount
  useEffect(() => {
    if (!features.identityVerification) {
      navigate("/", { replace: true });
      return;
    }
    dispatch(validateVerificationHash(hash))
      .then((res) => {
        setFirstName(res.Result?.FirstName || "");
        setStep(STEP.SELFIE);
      })
      .catch(() => {
        setErrorMsg("This verification link is invalid or has expired.");
        setStep(STEP.ERROR);
      });
  }, [hash, dispatch]);

  const handleSelfieFile = async (file) => {
    setUploading(true);
    setUploadError(null);
    try {
      const res = await dispatch(validateSelfieAnonymous(hash, file));
      setSelfieResult(res.Result);
      // Brief delay then move to document step
      setTimeout(() => setStep(STEP.DOCUMENT), 1500);
    } catch (err) {
      setUploadError(err?.Result || "Selfie validation failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const [manualReviewLoading, setManualReviewLoading] = useState(false);
  const handleRequestManualReview = async () => {
    setManualReviewLoading(true);
    try {
      await dispatch(requestManualReviewAnonymous(hash));
      setManualReviewRequested(true);
      setStep(STEP.COMPLETE);
    } catch {
      setManualReviewLoading(false);
    }
  };

  const handleDocFile = async (file) => {
    setUploading(true);
    setUploadError(null);
    try {
      const res = await dispatch(analyzeDocumentAnonymous(hash, file));
      setDocResult(res.Result);
      setTimeout(() => setStep(STEP.COMPLETE), 1500);
    } catch (err) {
      setUploadError(err?.Result || "Document verification failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // ─── Progress Bar ─────────────────────────────────────────────────────
  const stepIndex = step === STEP.SELFIE ? 0 : step === STEP.DOCUMENT ? 1 : 2;
  const progressPercent = step === STEP.COMPLETE ? 100 : ((stepIndex + 1) / 3) * 100;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start">
      <div className="w-full max-w-lg bg-white min-h-screen shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-[#9aa82d] px-5 py-4 flex items-center gap-3">
          <i className="fal fa-shield-check text-white text-xl"></i>
          <h1 className="text-base font-semibold text-white">Identity Verification</h1>
        </div>

        {/* Progress */}
        {step !== STEP.LOADING && step !== STEP.ERROR && (
          <div className="px-5 pt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Step {Math.min(stepIndex + 1, 3)} of 3</span>
              <span>{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-[#9aa82d] h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex-1 p-5">
          {/* Loading */}
          {step === STEP.LOADING && (
            <div className="flex items-center justify-center h-40 text-gray-500">Validating link...</div>
          )}

          {/* Error */}
          {step === STEP.ERROR && (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <i className="fal fa-exclamation-triangle text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Invalid Link</h2>
              <p className="text-sm text-gray-500">{errorMsg}</p>
            </div>
          )}

          {/* Selfie Step */}
          {step === STEP.SELFIE && (
            <>
              {firstName && (
                <p className="text-sm text-gray-600 mb-4">Hi {firstName}, let's verify your identity.</p>
              )}
              {uploading ? (
                <div className="flex flex-col items-center gap-3 py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9aa82d]"></div>
                  <p className="text-sm text-gray-500">Validating selfie...</p>
                </div>
              ) : selfieResult ? (
                <div className={`rounded-lg p-4 text-sm ${selfieResult.IsValid ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                  {selfieResult.IsValid ? "Selfie verified! Moving to document verification..." : "Selfie did not pass quality check."}
                  {selfieResult.RejectionReasons?.length > 0 && (
                    <ul className="mt-2 list-disc list-inside text-xs">
                      {selfieResult.RejectionReasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                  {!selfieResult.IsValid && (
                    <button
                      type="button"
                      onClick={() => { setSelfieResult(null); setUploadError(null); }}
                      className="mt-3 w-full py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors"
                    >
                      Retake Selfie
                    </button>
                  )}
                </div>
              ) : (
                <CameraCapture
                  facingMode="user"
                  onFile={handleSelfieFile}
                  onSkip={() => setStep(STEP.DOCUMENT)}
                  title="Take a Selfie"
                  description="We need a clear photo of your face. Make sure you are in a well-lit area."
                  icon="fa-camera"
                />
              )}
              {uploadError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{uploadError}</div>
              )}
            </>
          )}

          {/* Document Step */}
          {step === STEP.DOCUMENT && (
            <>
              {uploading ? (
                <div className="flex flex-col items-center gap-3 py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9aa82d]"></div>
                  <p className="text-sm text-gray-500">Analyzing document...</p>
                </div>
              ) : docResult ? (
                <div className={`rounded-lg p-4 text-sm ${
                  docResult.Status === "Valid"
                    ? "bg-green-50 text-green-700"
                    : docResult.Status === "Invalid"
                      ? "bg-red-50 text-red-600"
                      : "bg-yellow-50 text-yellow-700"
                }`}>
                  {docResult.Status === "Valid"
                    ? "Document verified! Completing verification..."
                    : docResult.Status === "Invalid"
                      ? "Document verification failed."
                      : "Document requires manual review."}
                  {docResult.Reasons?.length > 0 && (
                    <ul className="mt-2 list-disc list-inside text-xs">
                      {docResult.Reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                  {docResult.Status === "Invalid" && (
                    <button
                      type="button"
                      onClick={() => { setDocResult(null); setUploadError(null); }}
                      className="mt-3 w-full py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-100 text-sm font-medium transition-colors"
                    >
                      Retake Photo
                    </button>
                  )}
                </div>
              ) : (
                <CameraCapture
                  facingMode="environment"
                  onFile={handleDocFile}
                  onSkip={() => setStep(STEP.COMPLETE)}
                  title="Scan Your ID Document"
                  description="Take a photo of your government-issued ID (passport, driver's license, etc.)."
                  icon="fa-id-card"
                />
              )}
              {uploadError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{uploadError}</div>
              )}
            </>
          )}

          {/* Complete */}
          {step === STEP.COMPLETE && (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${manualReviewRequested ? "bg-yellow-100" : "bg-green-100"}`}>
                <i className={`fal text-3xl ${manualReviewRequested ? "fa-clock text-yellow-500" : "fa-check-circle text-green-500"}`}></i>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {manualReviewRequested ? "Manual review requested" : "Verification Complete"}
              </h2>
              <p className="text-sm text-gray-500">
                {manualReviewRequested
                  ? "An administrator will review your account and verify your identity."
                  : "Thank you for completing the identity verification process. You may now close this page."}
              </p>
            </div>
          )}

          {/* No camera option */}
          {step !== STEP.LOADING && step !== STEP.ERROR && step !== STEP.COMPLETE && !manualReviewRequested && (
            <div className="text-center pt-4">
              <button
                onClick={handleRequestManualReview}
                disabled={manualReviewLoading}
                className="text-sm text-gray-400 hover:text-gray-600 underline transition-colors disabled:opacity-50"
              >
                {manualReviewLoading ? "Requesting..." : "I don't have access to a camera"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyMobilePage;
